/** Forma de una nota sintetizada. */
interface Tone {
  readonly frequency: number;
  readonly durationMs: number;
  readonly type: OscillatorType;
}

const MASTER_VOLUME = 0.18;
const MUSIC_VOLUME = 0.08;
const MUSIC_BEAT_MS = 270;
const MUTE_STORAGE_KEY = 'maia-eyekon:muted';

/** Melodia de fondo (pentatonica mayor de Do, alegre) en corcheas, en bucle. */
const MUSIC_MELODY: readonly number[] = [
  659, 784, 880, 784, 659, 587, 659, 523, 587, 659, 784, 659, 587, 523, 440, 523,
];

/** Notas graves de acompanamiento, una por compas (cada 4 pasos). */
const MUSIC_BASS: readonly number[] = [131, 131, 165, 196];

/**
 * Servicio de sonido basado en la Web Audio API. Genera los efectos por codigo
 * (osciladores) en lugar de cargar archivos de audio, lo que mantiene el juego
 * liviano y sin dependencias de assets. Los navegadores exigen un gesto del usuario
 * para reproducir audio, por eso el contexto se reanuda al primer uso.
 */
export class AudioService {
  private context: AudioContext | null = null;
  private muted = false;

  private musicGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  /** Indica si el jugador quiere musica (se respeta al mutear/desmutear). */
  private musicWanted = false;

  public constructor() {
    this.muted = this.loadMuted();
  }

  public get isMuted(): boolean {
    return this.muted;
  }

  public toggleMute(): boolean {
    this.muted = !this.muted;
    this.persistMuted();

    if (this.muted) {
      this.stopMusic();
    } else if (this.musicWanted) {
      this.runMusic();
    }
    return this.muted;
  }

  /** Arranca la musica de fondo (se llama tras el primer gesto del usuario). */
  public startMusic(): void {
    this.musicWanted = true;
    if (!this.muted) {
      this.runMusic();
    }
  }

  /** Detiene la musica de fondo. */
  public stopMusic(): void {
    if (this.musicTimer !== null) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicGain !== null) {
      this.musicGain.disconnect();
      this.musicGain = null;
    }
  }

  public playClick(): void {
    this.playSequence([{ frequency: 520, durationMs: 70, type: 'triangle' }]);
  }

  public playHit(): void {
    this.playSequence([{ frequency: 660, durationMs: 80, type: 'sine' }]);
  }

  public playMiss(): void {
    this.playSequence([{ frequency: 180, durationMs: 140, type: 'sawtooth' }]);
  }

  public playCorrect(): void {
    this.playSequence([
      { frequency: 660, durationMs: 90, type: 'sine' },
      { frequency: 880, durationMs: 120, type: 'sine' },
    ]);
  }

  public playWrong(): void {
    this.playSequence([
      { frequency: 220, durationMs: 120, type: 'square' },
      { frequency: 160, durationMs: 160, type: 'square' },
    ]);
  }

  public playReward(): void {
    this.playSequence([
      { frequency: 523, durationMs: 90, type: 'triangle' },
      { frequency: 659, durationMs: 90, type: 'triangle' },
      { frequency: 784, durationMs: 140, type: 'triangle' },
    ]);
  }

  private runMusic(): void {
    const context = this.ensureContext();
    if (context === null || this.musicTimer !== null) {
      return;
    }

    this.musicGain = context.createGain();
    this.musicGain.gain.setValueAtTime(MUSIC_VOLUME, context.currentTime);
    this.musicGain.connect(context.destination);

    this.musicStep = 0;
    this.musicTimer = window.setInterval(() => this.tickMusic(context), MUSIC_BEAT_MS);
  }

  private tickMusic(context: AudioContext): void {
    if (this.muted || this.musicGain === null) {
      return;
    }

    const step = this.musicStep;
    const note = MUSIC_MELODY[step % MUSIC_MELODY.length];
    this.scheduleMusicTone(context, note, 'triangle', 230, 0.6);

    if (step % 4 === 0) {
      const bass = MUSIC_BASS[Math.floor(step / 4) % MUSIC_BASS.length];
      this.scheduleMusicTone(context, bass, 'sine', 620, 0.7);
    }

    this.musicStep = (step + 1) % (MUSIC_MELODY.length * 4);
  }

  private scheduleMusicTone(
    context: AudioContext,
    frequency: number,
    type: OscillatorType,
    durationMs: number,
    peak: number,
  ): void {
    if (this.musicGain === null) {
      return;
    }

    const now = context.currentTime;
    const endTime = now + durationMs / 1000;

    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

    oscillator.connect(gain);
    gain.connect(this.musicGain);

    oscillator.start(now);
    oscillator.stop(endTime + 0.02);
  }

  private loadMuted(): boolean {
    try {
      return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private persistMuted(): void {
    try {
      window.localStorage.setItem(MUTE_STORAGE_KEY, String(this.muted));
    } catch {
      // Almacenamiento no disponible: se ignora silenciosamente.
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.context === null) {
      try {
        this.context = new AudioContext();
      } catch (error) {
        console.error('Web Audio API no disponible en este navegador.', error);
        return null;
      }
    }
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
    return this.context;
  }

  private playSequence(tones: Tone[]): void {
    if (this.muted) {
      return;
    }
    const context = this.ensureContext();
    if (context === null) {
      return;
    }

    let startTime = context.currentTime;
    for (const tone of tones) {
      this.scheduleTone(context, tone, startTime);
      startTime += tone.durationMs / 1000;
    }
  }

  private scheduleTone(context: AudioContext, tone: Tone, startTime: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const endTime = startTime + tone.durationMs / 1000;

    oscillator.type = tone.type;
    oscillator.frequency.setValueAtTime(tone.frequency, startTime);

    gain.gain.setValueAtTime(MASTER_VOLUME, startTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start(startTime);
    oscillator.stop(endTime);
  }
}
